import { searchBarClick, searchPerformed, searchQueryEvent } from '@nulo/gtm';
import { createFocusTrap, safeFocus } from '@nulo/a11y';

const DEBOUNCE_MS = 500;
const PRODUCT_LIMIT = 8;

/**
 * @extends {HTMLElement}
 */
class NuloHeaderSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #debounceTimer = null;

  /** @type {number} */
  #scrollPosition = 0;

  /** @type {Record<string, unknown>} */
  #cachedResults = {};

  /** @type {string} */
  #defaultProductsHtml = '';

  /** @type {AbortController | null} */
  #fetchAbortController = null;

  /** @type {string} */
  #searchTerm = '';

  /** @type {(() => void) | null} */
  #focusTrapRelease = null;

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const productGrid = this.querySelector('[data-search-product-grid]');
    if (productGrid instanceof HTMLElement) {
      this.#defaultProductsHtml = productGrid.innerHTML;
    }

    this.querySelector('[data-search-form]')?.addEventListener('submit', (event) => this.#onSubmit(event), {
      signal,
    });
    this.querySelector('[data-search-input]')?.addEventListener('input', (event) => this.#onInput(event), {
      signal,
    });
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-search-dismiss]')?.addEventListener('click', () => this.close(), { signal });

    this.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('[data-search-product-link]') : null;
      if (link) this.close();
    });

    document.addEventListener('keydown', (event) => this.#onDocumentKeydown(event), { signal });
    document.addEventListener('nulo-search:open', () => this.open(), { signal });

    this.querySelectorAll('[data-search-popular-link]').forEach((link) => {
      link.addEventListener('click', () => this.close(), { signal });
    });

    // Capture href before close() — resetSearch() restores the default View All URL
    // (no ?q=), which would land on a blank search and trigger the dog default redirect.
    this.querySelectorAll('[data-search-view-all]').forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          if (!(link instanceof HTMLAnchorElement)) return;
          const href = link.href;
          if (!href) return;

          event.preventDefault();
          if (this.#searchTerm) searchPerformed(this.#searchTerm);
          this.close();
          window.location.assign(href);
        },
        { signal },
      );
    });
  }

  disconnectedCallback() {
    this.#clearDebounce();
    this.#releaseFocusTrap();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#setHeaderGroupState(false);
    this.#unlockBodyScroll();
  }

  open() {
    if (this.classList.contains('is-active')) return;

    this.hidden = false;
    this.#syncBarHeight();
    this.classList.add('is-active');
    this.querySelector('[data-search-overlay]')?.setAttribute('aria-hidden', 'false');
    this.#lockBodyScroll();
    this.#setToggleState(true);
    this.#setHeaderGroupState(true);
    this.#activateFocusTrap();
    this.dispatchEvent(new CustomEvent('nulo-search:opened', { bubbles: true }));

    const input = this.querySelector('[data-search-input]');
    if (input instanceof HTMLInputElement) {
      window.requestAnimationFrame(() => input.focus());
    }
  }

  close() {
    if (!this.classList.contains('is-active')) return;

    this.classList.remove('is-active');
    this.querySelector('[data-search-overlay]')?.setAttribute('aria-hidden', 'true');
    this.#releaseFocusTrap();
    this.#unlockBodyScroll();
    this.#resetSearch();
    this.#setToggleState(false);
    this.#setHeaderGroupState(false);
    this.dispatchEvent(new CustomEvent('nulo-search:closed', { bubbles: true }));

    window.setTimeout(() => {
      if (!this.classList.contains('is-active')) this.hidden = true;
    }, 300);
  }

  toggle() {
    if (this.classList.contains('is-active')) {
      this.close();
      return;
    }
    searchBarClick();
    this.open();
  }

  #activateFocusTrap() {
    const panel = this.querySelector('[data-search-panel]');
    if (!(panel instanceof HTMLElement)) return;

    this.#focusTrapRelease?.();
    this.#focusTrapRelease = createFocusTrap(panel, () => this.close());
  }

  #releaseFocusTrap() {
    this.#focusTrapRelease?.();
    this.#focusTrapRelease = null;
  }

  #syncBarHeight() {
    const header = document.querySelector('nulo-header');
    const bar = header?.querySelector('.nulo-header__bar');
    if (!(bar instanceof HTMLElement)) return;

    const height = Math.round(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--nulo-header-bar-height', `${height - 1}px`);
  }

  /**
   * @param {boolean} isOpen
   */
  #setHeaderGroupState(isOpen) {
    document.getElementById('header-group')?.classList.toggle('nulo-search-open', isOpen);
  }

  #setToggleState(isOpen) {
    document.querySelectorAll('[data-search-open]').forEach((button) => {
      button.classList.toggle('is-active', isOpen);
      if (button instanceof HTMLButtonElement) {
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });
  }

  #lockBodyScroll() {
    this.#scrollPosition = window.pageYOffset;
    document.body.classList.add('body-scroll-lock');
    document.body.style.setProperty('top', `-${this.#scrollPosition}px`, 'important');
  }

  #unlockBodyScroll() {
    if (!document.body.classList.contains('body-scroll-lock')) return;
    document.body.classList.remove('body-scroll-lock');
    document.body.style.removeProperty('top');
    window.scrollTo({ top: this.#scrollPosition, behavior: 'instant' });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown(event) {
    if (event.key !== 'Escape' || !this.classList.contains('is-active')) return;
    event.preventDefault();
    this.close();
  }

  /**
   * @param {Event} event
   */
  #onSubmit(event) {
    event.preventDefault();
    const input = this.querySelector('[data-search-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const term = input.value.trim();
    if (!term) {
      input.focus();
      return;
    }

    searchPerformed(term);
    this.close();
    window.location.href = this.#searchPageUrl(term);
  }

  /**
   * @param {Event} event
   */
  #onInput(event) {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;

    const term = input.value.trim();
    this.#searchTerm = term;

    this.#clearDebounce();
    if (!term) {
      this.#fetchAbortController?.abort();
      this.#fetchAbortController = null;
      this.#setLoading(false);
      this.#showDefaultResults();
      return;
    }

    this.#debounceTimer = window.setTimeout(() => {
      this.#fetchResults(term);
    }, DEBOUNCE_MS);
  }

  #clearDebounce() {
    if (this.#debounceTimer != null) {
      window.clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }
  }

  #resetSearch() {
    this.#clearDebounce();
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = null;
    this.#setLoading(false);
    this.#searchTerm = '';

    const input = this.querySelector('[data-search-input]');
    if (input instanceof HTMLInputElement) input.value = '';

    this.#showDefaultResults();
  }

  #showDefaultResults() {
    this.querySelector('[data-search-empty-message]')?.setAttribute('hidden', '');
    this.querySelector('[data-search-default-searches]')?.removeAttribute('hidden');
    this.querySelector('[data-search-live-collections]')?.setAttribute('hidden', '');
    this.querySelector('[data-search-live-blog]')?.setAttribute('hidden', '');
    this.#restoreDefaultProducts();

    const productsHeading = this.dataset.productsHeading || 'Popular Products';
    const heading = this.querySelector('[data-search-products-heading]');
    if (heading) heading.textContent = productsHeading;

    const viewAll = this.querySelector('[data-search-view-all]');
    if (viewAll instanceof HTMLAnchorElement) {
      viewAll.href = this.dataset.viewAllUrl || window.Theme?.routes?.search_url || '/search';
      const span = viewAll.querySelector('span');
      if (span) span.textContent = this.dataset.viewAllLabel || 'View All';
      viewAll.removeAttribute('hidden');
    }

    this.#syncSidebarScrollMode(false);
    this.#syncSidebarVisibility();
  }

  /**
   * @param {string} term
   */
  async #fetchResults(term) {
    const cacheKey = term.toLowerCase();
    if (this.#cachedResults[cacheKey]) {
      this.#renderResults(term, this.#cachedResults[cacheKey]);
      this.#setLoading(false);
      return;
    }

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();
    const { signal } = this.#fetchAbortController;

    this.#setLoading(true);

    try {
      const data = await this.#predictiveSearch(term, signal);
      if (signal.aborted) return;

      this.#cachedResults[cacheKey] = data;
      if (this.#searchTerm.toLowerCase() !== cacheKey) return;

      searchQueryEvent(term);
      this.#renderResults(term, data);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#showDefaultResults();
    } finally {
      if (!signal.aborted) this.#setLoading(false);
    }
  }

  /**
   * Shopify suggest.json returns only one resource type when multiple types are
   * requested in a single call — fetch each type in parallel instead.
   * Product total for View All comes from a lightweight section render
   * (search.results_count), not predictive suggest (capped at 8–10).
   *
   * @param {string} term
   * @param {AbortSignal} signal
   * @returns {Promise<{
   *   products: Array<Record<string, unknown>>,
   *   collections: Array<Record<string, unknown>>,
   *   articles: Array<Record<string, unknown>>,
   *   pages: Array<Record<string, unknown>>,
   *   productCount: number
   * }>}
   */
  async #predictiveSearch(term, signal) {
    const baseUrl = window.Theme?.routes?.predictive_search_url;
    if (!baseUrl) {
      return { products: [], collections: [], articles: [], pages: [], productCount: 0 };
    }

    const suggestPath = baseUrl.endsWith('.json') ? baseUrl : `${baseUrl}.json`;

    const [products, collections, articles, pages, productCount] = await Promise.all([
      this.#fetchSuggestTypeSafe(suggestPath, term, 'product', PRODUCT_LIMIT, signal),
      this.#fetchSuggestTypeSafe(suggestPath, term, 'collection', PRODUCT_LIMIT, signal),
      this.#fetchSuggestTypeSafe(suggestPath, term, 'article', PRODUCT_LIMIT, signal),
      this.#fetchSuggestTypeSafe(suggestPath, term, 'page', PRODUCT_LIMIT, signal),
      this.#fetchProductCount(term, signal),
    ]);

    return {
      products,
      collections,
      articles,
      pages,
      productCount: productCount || products.length,
    };
  }

  /**
   * Total product matches via Section Rendering API (search.results_count).
   * /search.json only returns a small result page, so it cannot drive View All.
   *
   * @param {string} term
   * @param {AbortSignal} signal
   * @returns {Promise<number>}
   */
  async #fetchProductCount(term, signal) {
    try {
      const searchUrl = window.Theme?.routes?.search_url || '/search';
      const url = new URL(searchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('sections', 'search-product-count');

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) return 0;

      const json = await response.json();
      const html = json?.['search-product-count'];
      if (typeof html !== 'string' || !html) return 0;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('[data-search-product-count]');
      const count = Number(el?.getAttribute('data-search-product-count'));
      return Number.isFinite(count) && count > 0 ? count : 0;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return 0;
    }
  }

  /**
   * @param {string} suggestPath
   * @param {string} term
   * @param {string} type
   * @param {number} limit
   * @param {AbortSignal} signal
   * @returns {Promise<Array<Record<string, unknown>>>}
   */
  async #fetchSuggestTypeSafe(suggestPath, term, type, limit, signal) {
    try {
      return await this.#fetchSuggestType(suggestPath, term, type, limit, signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return [];
    }
  }

  /**
   * @param {string} suggestPath
   * @param {string} term
   * @param {string} type
   * @param {number} limit
   * @param {AbortSignal} signal
   * @returns {Promise<Array<Record<string, unknown>>>}
   */
  async #fetchSuggestType(suggestPath, term, type, limit, signal) {
    const url = new URL(suggestPath, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', type);
    url.searchParams.set('resources[limit]', String(limit));

    const response = await fetch(url.toString(), { credentials: 'same-origin', signal });
    if (!response.ok) return [];

    const json = await response.json();
    const results = json?.resources?.results || {};
    const key = type === 'product' ? 'products' : `${type}s`;

    return results[key] || [];
  }

  /**
   * @param {string} term
   * @param {{
   *   products: Array<Record<string, unknown>>,
   *   collections: Array<Record<string, unknown>>,
   *   articles: Array<Record<string, unknown>>,
   *   pages: Array<Record<string, unknown>>,
   *   productCount?: number
   * }} data
   */
  #renderResults(term, data) {
    const products = data.products || [];
    const collections = data.collections || [];
    const articles = data.articles || [];
    const pages = (data.pages || []).filter((page) => String(page.url || '').includes('/blogs/'));
    const blogItems = [...articles, ...pages];
    const hasProductResults = products.length > 0;
    const hasSidebarResults = collections.length > 0 || blogItems.length > 0;
    const hasAnyResults = hasProductResults || hasSidebarResults;
    const productCount = Math.max(Number(data.productCount) || 0, products.length);

    this.querySelector('[data-search-default-searches]')?.setAttribute('hidden', '');

    const emptyMessage = this.querySelector('[data-search-empty-message]');

    if (!hasAnyResults) {
      const template = this.dataset.noResultsTemplate || '';
      if (emptyMessage instanceof HTMLElement) {
        emptyMessage.textContent = template.replace('$searchTerm', term);
        emptyMessage.removeAttribute('hidden');
      }
      this.#showDefaultResults();
      return;
    }

    emptyMessage?.setAttribute('hidden', '');

    this.#renderSidebarLinks('[data-search-collections-list]', collections, '[data-search-live-collections]');
    this.#renderSidebarLinks('[data-search-blog-list]', blogItems, '[data-search-live-blog]');

    const heading = this.querySelector('[data-search-products-heading]');
    if (heading) heading.textContent = this.dataset.resultsHeading || 'Search Results';

    if (hasProductResults) {
      this.#renderLiveProducts(products);
    } else {
      this.#restoreDefaultProducts();
    }

    const viewAll = this.querySelector('[data-search-view-all]');
    if (viewAll instanceof HTMLAnchorElement) {
      if (hasProductResults) {
        viewAll.href = this.#searchPageUrl(term);
        const span = viewAll.querySelector('span');
        if (span) span.textContent = this.#viewAllLabel(productCount);
        viewAll.removeAttribute('hidden');
      } else {
        viewAll.setAttribute('hidden', '');
      }
    }

    this.#syncSidebarScrollMode(collections.length > 0);
    this.#syncSidebarVisibility();
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #viewAllLabel(count) {
    if (count <= 0) return this.dataset.viewAllLabel || 'View All';
    const template = this.dataset.viewAllCount || 'View All COUNT_PLACEHOLDER Products';
    return template.replace('COUNT_PLACEHOLDER', String(count));
  }

  /**
   * @param {string} listSelector
   * @param {Array<Record<string, unknown>>} items
   * @param {string} groupSelector
   */
  #renderSidebarLinks(listSelector, items, groupSelector) {
    const group = this.querySelector(groupSelector);
    const list = this.querySelector(listSelector);

    if (!(group instanceof HTMLElement) || !(list instanceof HTMLUListElement) || items.length === 0) {
      group?.setAttribute('hidden', '');
      return;
    }

    list.replaceChildren();
    for (const item of items) {
      const title = String(item.title || '');
      const url = String(item.url || '');
      if (!title || !url) continue;

      const li = document.createElement('li');
      li.setAttribute('role', 'listitem');
      const link = document.createElement('a');
      link.href = url;
      link.className = 'nulo-search__link';
      link.textContent = title;
      link.addEventListener('click', () => this.close());
      li.append(link);
      list.append(li);
    }

    group.removeAttribute('hidden');
  }

  /**
   * @param {boolean} hasCollections
   */
  #syncSidebarScrollMode(hasCollections) {
    const sidebar = this.querySelector('[data-search-sidebar]');
    if (!(sidebar instanceof HTMLElement)) return;
    sidebar.classList.toggle('nulo-search__sidebar--compact-scroll', hasCollections);
  }

  #syncSidebarVisibility() {
    const sidebar = this.querySelector('[data-search-sidebar]');
    const defaultSearches = this.querySelector('[data-search-default-searches]');
    const liveCollections = this.querySelector('[data-search-live-collections]');
    const liveBlog = this.querySelector('[data-search-live-blog]');
    if (!(sidebar instanceof HTMLElement)) return;

    const hasDefault =
      defaultSearches instanceof HTMLElement &&
      !defaultSearches.hasAttribute('hidden') &&
      defaultSearches.querySelector('.nulo-search__link-list li') != null;
    const hasLive =
      (liveCollections instanceof HTMLElement && !liveCollections.hasAttribute('hidden')) ||
      (liveBlog instanceof HTMLElement && !liveBlog.hasAttribute('hidden'));

    sidebar.hidden = !hasDefault && !hasLive;
  }

  #restoreDefaultProducts() {
    const grid = this.querySelector('[data-search-product-grid]');
    if (!(grid instanceof HTMLElement)) return;
    grid.innerHTML = this.#defaultProductsHtml;
  }

  /**
   * @param {Array<Record<string, unknown>>} products
   */
  #renderLiveProducts(products) {
    const grid = this.querySelector('[data-search-product-grid]');
    if (!(grid instanceof HTMLElement)) return;

    grid.replaceChildren();

    const seenTitles = new Set();
    for (const product of products) {
      const titleKey = String(product.title || '')
        .trim()
        .toLowerCase();
      if (!titleKey || seenTitles.has(titleKey)) continue;
      seenTitles.add(titleKey);

      grid.append(this.#createProductCard(product));
    }
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {HTMLElement}
   */
  #createProductCard(product) {
    const template = this.querySelector('[data-search-product-template]');
    const fragment = template?.content.cloneNode(true);
    const article = fragment instanceof DocumentFragment ? fragment.querySelector('article') : null;

    if (!(article instanceof HTMLElement)) {
      const fallback = document.createElement('article');
      fallback.className = 'nulo-search-product';
      return fallback;
    }

    const title = String(product.title || '');
    const url = String(product.url || '');
    const image = String(product.image || product.featured_image?.url || '');

    article.querySelectorAll('[data-search-product-link]').forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = url;
        if (link.classList.contains('nulo-search-product__title')) {
          link.textContent = title;
        }
      }
    });

    const img = article.querySelector('.nulo-search-product__image');
    if (img instanceof HTMLImageElement) {
      img.src = image;
      img.alt = title;
      if (!image) img.remove();
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const sizes = article.querySelector('[data-search-product-sizes]');
    const sizeLabel = this.#productSizeLabel(product, variants.length);

    if (sizes instanceof HTMLElement && sizeLabel) {
      sizes.textContent = sizeLabel;
      sizes.removeAttribute('hidden');
    }

    return article;
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {number} variantCount
   * @returns {string}
   */
  #productSizeLabel(product, variantCount) {
    const tags = Array.isArray(product.tags) ? product.tags : [];
    const sizeTag = tags.find((tag) => String(tag).startsWith('sizeCount_'));
    if (sizeTag) return String(sizeTag).replace('sizeCount_', '');

    if (variantCount > 0) return this.#sizesLabel(variantCount);
    return '';
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #sizesLabel(count) {
    const one = this.dataset.sizesOne || '1 Size Available';
    const otherTemplate = this.dataset.sizesOther || 'COUNT_PLACEHOLDER Sizes Available';
    if (count === 1) return one;
    return otherTemplate.replace('COUNT_PLACEHOLDER', String(count));
  }

  /**
   * @param {string} term
   * @returns {string}
   */
  #searchPageUrl(term) {
    const base = window.Theme?.routes?.search_url || '/search';
    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    return url.pathname + url.search;
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const loading = this.querySelector('[data-search-loading]');
    const submit = this.querySelector('[data-search-submit]');
    if (loading instanceof HTMLElement) loading.hidden = !isLoading;
    if (submit instanceof HTMLButtonElement) submit.hidden = isLoading;
  }
}

if (!customElements.get('nulo-header-search')) {
  customElements.define('nulo-header-search', NuloHeaderSearch);
}
